import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";
import Decimal from "decimal.js";

interface UseSpinningWheelThresholdProgressOptions {
  enabled?: boolean;
}

const DEFAULT_SPINNING_WHEEL_PROGRESS = {
  currentUsdValue: new Decimal(0),
  thresholdUsdValue: new Decimal(2.0),
  isThresholdReached: false,
  progressPercentage: 0,
  pendingResultsCount: 0,
};

/**
 * Hook to fetch threshold progress for authenticated user's spinning wheel rewards
 * Shows how close the user is to reaching the minimum claimable threshold ($2 default)
 *
 * Returns:
 * - currentUsdValue: Current USD value of pending rewards
 * - thresholdUsdValue: Threshold USD value required to claim
 * - isThresholdReached: Whether threshold has been reached
 * - progressPercentage: Progress percentage (0-100)
 * - pendingResultsCount: Count of pending results (not yet assigned epoch_id)
 */
export const useSpinningWheelThresholdProgress = (options?: UseSpinningWheelThresholdProgressOptions) => {
  const { hubUser, auth } = useHubUser();
  const apiHelper = useApi();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [QueryKeys.SPINNING_WHEEL_THRESHOLD_PROGRESS, hubUser?.id],
    queryFn: async () => {
      return await apiHelper.getWithAuth(auth, API_ENDPOINT.SPINNING_WHEEL_THRESHOLD_PROGRESS);
    },
    enabled: (options?.enabled ?? true) && !!hubUser?.id,
    staleTime: 30 * 1000, // 30 seconds
    retry: 2,
  });

  const resultData = data ?? DEFAULT_SPINNING_WHEEL_PROGRESS;

  return {
    ...resultData,
    isLoading,
    error: error?.message ?? null,
    refetch,
  };
};
